import React from "react";
import { generateUnitPattern, getUnitCategory, UnitCategory } from "@ezrecipebox/shared";

type HighlightSpan = {
  text: string;
  type: "number" | "fraction" | "unit" | "normal";
  category?: UnitCategory;
};

/**
 * Utility class for highlighting numbers, fractions, and units in recipe ingredients
 */
export class IngredientHighlighter {
  // Regex pattern for whole numbers and decimals (e.g., "2", "2.5", "100")
  private static readonly numberPatternSource = "\\b\\d+(\\.\\d+)?\\b";

  // Regex pattern for fractions and mixed numbers
  // Matches: "1/2", "3/4" (simple fractions) and "1 1/2", "2 3/4" (mixed numbers)
  // Mixed number pattern: optional whole number, space(s), fraction
  private static readonly fractionPatternSource = "\\b\\d+\\s+\\d+\\/\\d+\\b|\\b\\d+\\/\\d+\\b";

  // Regex pattern for common cooking units (case-insensitive) - shared across platforms
  private static readonly unitPatternSource = generateUnitPattern();

  /**
   * Parses ingredient text and returns an array of spans with highlighting information
   * Enhanced Version 2: Better overlap handling with prioritized match types
   */
  static parseIngredient(text: string): HighlightSpan[] {
    if (!text || text.trim().length === 0) {
      return [{ text, type: "normal" }];
    }

    const spans: HighlightSpan[] = [];
    const matches: Array<{ 
      start: number; 
      end: number; 
      type: "number" | "fraction" | "unit"; 
      priority: number;
      category?: UnitCategory;
    }> = [];

    // Priority order: fractions (3) > numbers (2) > units (1)
    // Higher priority means it should be preferred when overlaps occur
    
    // Find fraction matches first (highest priority)
    let match;
    const fractionPattern = new RegExp(IngredientHighlighter.fractionPatternSource, "g");
    while ((match = fractionPattern.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: "fraction",
        priority: 3,
      });
    }

    // Find number matches (medium priority)
    const numberPattern = new RegExp(IngredientHighlighter.numberPatternSource, "g");
    while ((match = numberPattern.exec(text)) !== null) {
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;
      
      // Check if this number is part of a mixed number (already covered by fraction)
      const isPartOfMixedNumber = matches.some(
        (m) => m.type === "fraction" && m.start <= matchStart && m.end >= matchEnd
      );
      
      if (!isPartOfMixedNumber) {
        matches.push({
          start: matchStart,
          end: matchEnd,
          type: "number",
          priority: 2,
        });
      }
    }

    // Find unit matches (lowest priority)
    // Create pattern once for performance (Version 3 optimization)
    const unitPattern = new RegExp(IngredientHighlighter.unitPatternSource, "gi");
    while ((match = unitPattern.exec(text)) !== null) {
      const unitText = match[0];
      const category = getUnitCategory(unitText);
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: "unit",
        priority: 1,
        category,
      });
    }

    // Sort matches: first by start position, then by priority (descending)
    // This ensures that when overlaps occur, higher priority matches are processed first
    matches.sort((a, b) => {
      if (a.start !== b.start) {
        return a.start - b.start;
      }
      return b.priority - a.priority; // Higher priority first
    });

    // Build spans with improved overlap handling
    const processed = new Set<number>();
    let currentIndex = 0;
    
    for (const match of matches) {
      // Skip if any part of this match is already processed
      let alreadyProcessed = false;
      for (let i = match.start; i < match.end; i++) {
        if (processed.has(i)) {
          alreadyProcessed = true;
          break;
        }
      }

      if (alreadyProcessed) continue;

      // Add text before this match
      if (match.start > currentIndex) {
        const textBefore = text.substring(currentIndex, match.start);
        if (textBefore) {
          spans.push({ text: textBefore, type: "normal" });
        }
      }

      // Add highlighted match with category information
      const matchText = text.substring(match.start, match.end);
      spans.push({ 
        text: matchText, 
        type: match.type,
        category: match.category,
      });

      // Mark all positions in this match as processed
      for (let i = match.start; i < match.end; i++) {
        processed.add(i);
      }

      currentIndex = Math.max(currentIndex, match.end);
    }

    // Add remaining text
    if (currentIndex < text.length) {
      const remainingText = text.substring(currentIndex);
      if (remainingText) {
        spans.push({ text: remainingText, type: "normal" });
      }
    }

    // If no matches found, return plain text
    if (spans.length === 0) {
      return [{ text, type: "normal" }];
    }

    return spans;
  }

  /**
   * Get styling class for a unit category (Version 3: Custom styling per category)
   */
  private static getUnitStyleClass(category?: UnitCategory): string {
    if (!category) {
      return "italic text-gray-600"; // Default unit style
    }

    switch (category) {
      case UnitCategory.VOLUME:
        return "italic text-blue-600"; // Volume units: blue
      case UnitCategory.WEIGHT:
        return "italic text-purple-600"; // Weight units: purple
      case UnitCategory.SMALL_QUANTITY:
        return "italic text-green-600"; // Small quantities: green
      case UnitCategory.COUNTABLE:
        return "italic text-teal-600"; // Countable items: teal
      case UnitCategory.LENGTH:
        return "italic text-indigo-600"; // Length measurements: indigo
      default:
        return "italic text-gray-600";
    }
  }

  /**
   * Renders ingredient text with highlighting applied
   * Returns an array of React elements (spans) with appropriate styling
   * Version 3: Custom styling per category
   */
  static renderHighlightedIngredient(text: string): React.ReactNode[] {
    const spans = IngredientHighlighter.parseIngredient(text);

    return spans.map((span, index) => {
      switch (span.type) {
        case "number":
        case "fraction":
          return (
            <span key={index} className="font-bold text-orange-600">
              {span.text}
            </span>
          );
        case "unit":
          return (
            <span key={index} className={IngredientHighlighter.getUnitStyleClass(span.category)}>
              {span.text}
            </span>
          );
        default:
          return <span key={index}>{span.text}</span>;
      }
    });
  }
}

